import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";

import { ProtectedRoute, PublicRoute } from "router/route.utils";
import { LoadingFallback } from "components/common";
import ROUTES from "./routes.constants";
import { NavigationTracker } from "services/analytics";



// Layout components
import MainLayout from "components/layouts/MainLayout";
import KYCPage from "pages/onboarding/KYCPage";
// Page components with lazy loading
const ExplorePage = lazy(() => import("pages/ExplorePage"));
const InvestmentDetailsPage = lazy(() => import("pages/InvestmentDetailsPage"));

// Auth pages
const LoginPage = lazy(() => import("pages/auth/LoginPage"));
const SignupPage = lazy(() => import("pages/auth/SignupPage"));

// Onboarding pages
const RiskAssessmentPage = lazy(() =>
  import("pages/onboarding/RiskAssessmentPage")
);
const OnboardingStatusPage = lazy(() =>
  import("pages/onboarding/OnboardingStatusPage")
);
const BankLinkingPage = lazy(() => import("pages/onboarding/BankLinkingPage"));
// const KYCPage = lazy(() => import("pages/onboarding/KYCPage"));

const KYCOfflinePage = lazy(() => import("pages/onboarding/KYCOfflinePage"));
const FATCADetailsPage = lazy(() =>
  import("pages/onboarding/FATCADetailsPage")
);
const NomineeDetailsPage = lazy(() =>
  import("pages/onboarding/NomineeDetailsPage")
);

// Mutual funds pages
const MutualFundsPage = lazy(() =>
  import("pages/mutual-funds/MutualFundsPage")
);
const FundDetailsPage = lazy(() =>
  import("pages/mutual-funds/FundDetailsPage")
);
const AdvanceMFDetailPage = lazy(() =>
  import("pages/mutual-funds/AdvanceMFDetailPage")
);
const MFCollectionDetailsPage = lazy(() =>
  import("pages/MFCollectionDetailsPage")
);
const NfoFundsPage = lazy(() => import("pages/explore/NfoFundsPage"));
const NfoFundDetailsPage = lazy(() => import("pages/explore/NfoFundDetailsPage"));

// Portfolio pages
const PortfolioMutualFundPage = lazy(() =>
  import("pages/portfolio/PortfolioMutualFundPage")
);
const PortfolioFundDetailsPage = lazy(() =>
  import("pages/portfolio/PortfolioFundDetailsPage")
);
const PortfolioOrderPage = lazy(() =>
  import("pages/portfolio/PortfolioOrderPage")
);
const OrderDetailsPage = lazy(() => import("pages/portfolio/OrderDetailsPage"));
const PortfolioSummaryPage = lazy(() =>
  import("pages/portfolio/PortfolioSummaryPage")
);
const BondDetailPage = lazy(() =>
  import("pages/portfolio/BondDetailPage")
);

// Redeem funds pages
const RedeemFundsPage = lazy(() => import('pages/RedeemFundsPage'));

// SIP pages
const SystematicTransactionPage = lazy(() => import("pages/sip/SystematicTransactionPage"));
const SIPsPage = lazy(() => import("pages/sip/SIPsPage"));
const StpPage = lazy(() => import("pages/sip/StpPage"));
const SwpPage = lazy(() => import("pages/sip/SwpPage"));
const SWPFundDetailsPage = lazy(() => import("pages/sip/SWPFundDetailsPage"));
const STPFundDetailsPage = lazy(() => import("pages/sip/STPFundDetailsPage"));

import KRAConfirmationPage from "pages/onboarding/KRAConfirmationPage";
import FinfactorDashboardPage from "pages/FinfactorDashboardPage";
import FinfactorBankAccountPage from "pages/FinfactorBankAccountPage";
import FinfactorBankDetailPage from "pages/FinfactorBankDetailPage";

const ActiveSIPsPage = lazy(() => import("pages/sip/ActiveSIPsPage"));
const SIPsSummaryPage = lazy(() => import("pages/sip/SIPsSummaryPage"));
const SIPDetailsPage = lazy(() => import("pages/sip/SIPDetailsPage"));

// Notification pages
const NotificationsPage = lazy(() => import("pages/NotificationsPage"));
const GeneralNotificationsPage = lazy(() =>
  import("pages/notifications/GeneralNotificationsPage")
);
const ImportantNotificationsPage = lazy(() =>
  import("pages/notifications/ImportantNotificationsPage")
);

// Profile pages
const ProfileLayoutPage = lazy(() =>
  import("pages/profile/ProfileLayoutPage")
);
const PersonalDetailsPage = lazy(() =>
  import("pages/profile/PersonalDetailsPage")
);
const ProfileNomineeDetailsPage = lazy(() =>
  import("pages/profile/ProfileNomineeDetailsPage")
);
const ProfileBankDetailsPage = lazy(() =>
  import("pages/profile/ProfileBankDetailsPage")
);
const ProfileAddBankAccountPage = lazy(() =>
  import("pages/profile/ProfileAddBankAccountPage")
);
const ProfileAddNomineePage = lazy(() =>
  import("pages/profile/ProfileAddNomineePage")
);
const ProfileMandatesPage = lazy(() =>
  import("pages/profile/ProfileMandatesPage")
);
const ActivePlanPage = lazy(() =>
  import("pages/profile/ActivePlanPage")
);
const NewRequestPage = lazy(() =>
  import("pages/profile/NewRequestPage")
);

// Other pages
const ReportsPage = lazy(() => import("pages/ReportsPage"));
const HelpCenterPage = lazy(() => import("pages/HelpCenterPage"));
const InvestmentPortfolioPage = lazy(() => import("pages/InvestmentPortfolioPage"));
const BrokerListingPage = lazy(() => import("pages/BrokerListingPage"));
const BrokerDetailPage = lazy(() => import("pages/BrokerDetailPage"));
const InvestmentsPage = lazy(() => import("pages/InvestmentsPage"));
const InsurancePage = lazy(() => import("pages/InsurancePage"));

const NotFoundPage = lazy(() => import("pages/NotFoundPage"));

// Mobile and static pages
const SplashPage = lazy(() => import('pages/SplashPage'));
const TermsAndConditionsPage = lazy(() => import('pages/TermsAndConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('pages/PrivacyPolicyPage'));

const PanCardPage = lazy(() => import('pages/kyc/PanCardPage'));
const AadharCardPage = lazy(() => import('pages/kyc/AadharCardPage'));
const DocumentUploadPage = lazy(() => import('pages/kyc/DocumentUploadPage'));

// TODO: pages to be moved to onboarding pages and imports to be cleaned

// Router configuration
const router = createBrowserRouter([
  {
    // Root wrapper: fires screen_view on every navigation automatically
    element: <NavigationTracker />,
    children: [

  // Root route - redirect to explore for authenticated users
  {
    path: "/",
    element: <Navigate to={ROUTES.EXPLORE} replace />,
  },

  // Mobile-specific routes (no MainLayout)
  {
    path: ROUTES.SPLASH,
    element: (
      <Suspense fallback={<LoadingFallback fullPage />}>
        <SplashPage />
      </Suspense>
    ),
  },

  // Static/Legal pages (no MainLayout)
  {
    path: ROUTES.TERMS_AND_CONDITIONS,
    element: (
      <Suspense fallback={<LoadingFallback fullPage />}>
        <TermsAndConditionsPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.PRIVACY_POLICY,
    element: (
      <Suspense fallback={<LoadingFallback fullPage />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },

  {
    path: ROUTES.LOGIN,
    element: (
      <PublicRoute>
        <Suspense fallback={<LoadingFallback fullPage />}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.SIGNUP,
    element: (
      <PublicRoute>
        <Suspense fallback={<LoadingFallback fullPage />}>
          <SignupPage />
        </Suspense>
      </PublicRoute>
    ),
  },

  // Protected routes (all under MainLayout)
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback fullPage />}>
          <MainLayout />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      // Onboarding routes
      {
        path: ROUTES.RISK_ASSESSMENT.substring(1),
        element: <RiskAssessmentPage />,
      },
      {
        path: ROUTES.ONBOARDING_STATUS.substring(1),
        element: <OnboardingStatusPage />,
      },
      {
        path: ROUTES.ONBOARDING_BANK_LINKING.substring(1),
        element: <BankLinkingPage />,
      },
      {
        path: ROUTES.ONBOARDING_KYC.substring(1),
        element: <KYCPage />,
      },
      {
        path: ROUTES.ONBOARDING_KRA_KYC.substring(1),
        element: <KRAConfirmationPage />,
      },
      {
        path: ROUTES.ONBOARDING_KYC_OFFLINE.substring(1),
        element: <KYCOfflinePage />,
      },
      {
        path: ROUTES.ONBOARDING_FATCA_DETAILS.substring(1),
        element: <FATCADetailsPage />,
      },
      {
        path: ROUTES.ONBOARDING_NOMINEE_DETAILS.substring(1),
        element: <NomineeDetailsPage />,
      },
      {
        // Mandate creation is a modal now; keep the old link working by
        // landing on the mandates list with the modal opened.
        path: ROUTES.CREATE_MANDATE.substring(1),
        element: <Navigate to={`${ROUTES.PROFILE_MANDATES}?createMandate=1`} replace />,
      },

      // Main app routes
      {
        path: ROUTES.EXPLORE.substring(1),
        element: <ExplorePage />,
      },
      {
        path: ROUTES.INVESTMENTS.substring(1),
        element: <InvestmentsPage />,
      },
      {
        path: ROUTES.INVESTMENT_PORTFOLIO.substring(1),
        element: <InvestmentPortfolioPage />,
      },
      {
        path: ROUTES.INVESTMENT_PORTFOLIO_DETAILS.substring(1),
        element: <BrokerListingPage />,
      },
      {
        path: ROUTES.INVESTMENT_PORTFOLIO_BROKER.substring(1),
        element: <BrokerDetailPage />,
      },
      {
        path: ROUTES.INVESTMENT_PORTFOLIO_ORDER.substring(1),
        element: <BrokerDetailPage />,
      },
      {
        path: ROUTES.FINANCIAL_OVERVIEW.substring(1),
        element: <FinfactorDashboardPage />,
      },
      {
        path: ROUTES.FINFACTOR_BANKING.substring(1),
        element: <FinfactorBankAccountPage />,
      },
      {
        path: ROUTES.FINFACTOR_BANK_DETAILS.substring(1),
        element: <FinfactorBankDetailPage />,
      },
      {
        path: ROUTES.FINFACTOR_INSURANCE.substring(1),
        element: <InsurancePage />,
      },

      // Mutual funds routes
      {
        path: ROUTES.MUTUAL_FUNDS.substring(1),
        element: <MutualFundsPage />,
      },
      // {
      //   path: ROUTES.MUTUAL_FUNDS_FUND_ID.substring(1),
      //   element: <FundDetailsPage />,
      // },
      {
        path: ROUTES.MUTUAL_FUNDS_FUND_ID.substring(1),
        element: <AdvanceMFDetailPage />,
      },
      {
        path: ROUTES.EXPLORE_COLLECTION_DETAILS.substring(1),
        element: <MFCollectionDetailsPage />,
      },
      {
        path: ROUTES.EXPLORE_NFO.substring(1),
        element: <NfoFundsPage />,
      },
      {
        path: ROUTES.EXPLORE_NFO_FUND_ID.substring(1),
        element: <NfoFundDetailsPage />,
      },

      // Portfolio routes
      {
        path: ROUTES.PORTFOLIO.substring(0),
        element: <Navigate to={ROUTES.PORTFOLIO_SUMMARY} replace />,
      },
      {
        path: ROUTES.PORTFOLIO_MUTUAL_FUND.substring(1),
        element: <PortfolioMutualFundPage />,
      },
      {
        path: ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID.substring(1),
        element: <PortfolioFundDetailsPage />,
      },
      {
        path: ROUTES.PORTFOLIO_MUTUAL_FUND_REDEEM.substring(1),
        element: <RedeemFundsPage />,
      },
      {
        path: ROUTES.PORTFOLIO_ORDER.substring(1),
        element: <PortfolioOrderPage />,
      },
      {
        path: ROUTES.PORTFOLIO_ORDER_ORDER_ID.substring(1),
        element: <OrderDetailsPage />,
      },
      {
        path: ROUTES.PORTFOLIO_SUMMARY.substring(1),
        element: <PortfolioSummaryPage />,
      },
      {
        path: ROUTES.PORTFOLIO_BOND.substring(1),
        element: <BondDetailPage />,
      },

      // Redeem funds routes
      {
        path: ROUTES.REDEEM_FUNDS.substring(1),
        element: <RedeemFundsPage />,
      },


      // KYC routes
      {
        path: ROUTES.PAN_CARD.substring(1),
        element: <PanCardPage />,
      },
      {
        path: ROUTES.AADHAR_CARD.substring(1),
        element: <AadharCardPage />,
      },
      {
        path: ROUTES.DOCUMENT_UPLOAD.substring(1),
        element: <DocumentUploadPage />,
      },

      // Systematic Investment routes
      {
        path: ROUTES.SYSTEMATIC_INVESTMENT.substring(1),
        element: <SystematicTransactionPage />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.SIP_ROOT} replace />,
          },
          {
            path: "sip",
            element: <SIPsPage />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.SIP_ACTIVE} replace />,
              },
              {
                path: "active",
                element: <ActiveSIPsPage />,
              },
              {
                path: "summary",
                element: <SIPsSummaryPage />,
              },
              {
                path: "mandates",
                element: <ProfileMandatesPage />,
              },
            ],
          },
          {
            path: "stp",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.STP_ACTIVE} replace />,
              },
              {
                path: "active",
                element: <StpPage />,
              },
              {
                path: ":stpId",
                element: <STPFundDetailsPage />,
              },
            ],
          },
          {
            path: "swp",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.SWP_ACTIVE} replace />,
              },
              {
                path: "active",
                element: <SwpPage />,
              },
              {
                path: ":swpId",
                element: <SWPFundDetailsPage />,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.SIP_DETAILS.substring(1),
        element: <SIPDetailsPage />,
      },
      // {
      //   path: ROUTES.SIP_DETAIL.substring(1),
      //   element: <SIPDetailsPage />,
      // },

      // Notification routes
      {
        path: ROUTES.NOTIFICATIONS.substring(1),
        element: <Navigate to={ROUTES.NOTIFICATIONS_GENERAL} replace />,
      },
      {
        path: ROUTES.NOTIFICATIONS_GENERAL.substring(1),
        element: <NotificationsPage />
      },
      {
        path: ROUTES.NOTIFICATIONS_IMPORTANT.substring(1),
        element: <NotificationsPage />
      },

      // Profile routes — the five tabs are nested under a shared layout so
      // switching tabs swaps only the tab content, leaving the profile
      // header, relationship manager and tab strip mounted.
      {
        path: ROUTES.PROFILE.substring(1),
        element: <ProfileLayoutPage />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.PROFILE_PERSONAL_DETAILS} replace />,
          },
          {
            path: "personal-details",
            element: <PersonalDetailsPage />,
          },
          {
            path: "nominee-details",
            element: <ProfileNomineeDetailsPage />,
          },
          {
            path: "bank-details",
            element: <ProfileBankDetailsPage />,
          },
          {
            path: "my-plan",
            element: <ActivePlanPage />,
          },
          {
            path: "mandates",
            element: <ProfileMandatesPage />,
          },
        ],
      },
      // Standalone profile screens — full-page forms, no tab chrome.
      {
        path: ROUTES.PROFILE_ADD_BANK_ACCOUNT.substring(1),
        element: <ProfileAddBankAccountPage />,
      },
      {
        path: ROUTES.PROFILE_ADD_NOMINEE.substring(1),
        element: <ProfileAddNomineePage />,
      },
      {
        path: ROUTES.PROFILE_NEW_REQUEST.substring(1),
        element: <NewRequestPage />,
      },


      // Other routes
      {
        path: ROUTES.REPORTS.substring(1),
        element: <ReportsPage />,
      },
      {
        path: ROUTES.HELP_CENTER.substring(1),
        element: <HelpCenterPage />,
      },
    ],
  },

  // Not found route
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFoundPage />,
  },

    ], // end NavigationTracker children
  },   // end NavigationTracker wrapper
]);

export default router;
